import React from 'react'
import FileUploader from 'react-firebase-file-uploader'
import { Button } from '@material-ui/core'
import LinearProgress from '@material-ui/core/LinearProgress'
import ImageIcon from '@material-ui/icons/ImageOutlined'
import CloudDoneIcon from '@material-ui/icons/CloudDone'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import Img from './Img'
import { storage, userImagesRef } from '../utilities/firebase'
import styles from './UploadImage.icss'
import Icon from './Icon'

class UploadImage extends React.Component {
	static getDerivedStateFromProps(props, state) {
		return { imageContent: state.imageContent !== undefined ? state.imageContent : props.imageContent }
	}

	constructor(props) {
		super(props)
		this.state = {
			isUploading: false,
			progress: 0
		}
		this.storageRef = storage.ref(userImagesRef)
		this.handleUploadStart = this.handleUploadStart.bind(this)
		this.handleProgress = this.handleProgress.bind(this)
		this.handleUploadError = this.handleUploadError.bind(this)
		this.handleUploadSuccess = this.handleUploadSuccess.bind(this)
	}

	componentWillUnmount() {
		clearTimeout(this.state.resetTimeout)
	}

	handleUploadStart() {
		this.setState({ isUploading: true, progress: 0, imageContent: null })
	}

	handleProgress(progress) {
		this.setState({ progress })
	}

	handleUploadError(error) {
		this.setState({ isUploading: false })
		this.props.errorHandler(error)
	}

	handleUploadSuccess(filename) {
		this.setState({
			progress: 100,
			isUploading: false,
			//	This will reset the uploader
			resetTimeout: setTimeout(() => {
				this.setState({ progress: 0 })
			}, this.props.resetTimeout)
		})
		storage
			.ref(userImagesRef)
			.child(filename)
			.getDownloadURL()
			.then((imageURL) => {
				this.setState({ imageContent: imageURL }, () => this.props.saveUploadedImage(imageURL))
			})
	}

	render() {
		const { classNames = { surround: '', wrapper: '', buttonRoot: '', buttonIcon: '', progress: '' }, imgInline, imgClass, showChangeButton } = this.props
		const imageContent = this.state.imageContent
		const maxWidth = 10800

		return (
			<>
				{imageContent && imgInline && (
					<div className={styles.imgSurround}>
						<Img content={imageContent} className={imgClass} />
						{showChangeButton && (
							<div className={styles.changeImg}>
								<Button variant="contained" classes={{ root: styles.uploadButton }}>
									<span>
										{this.props.buttonIconName ? (
											<Icon name={this.props.buttonIconName} className={classNames.buttonIcon} />
										) : (
											<ImageIcon className={styles.icon} />
										)}
										<span>{this.props.buttonChangeText !== undefined ? this.props.buttonChangeText : this.context.translate('Change photo')}</span>
									</span>
								</Button>
								<FileUploader
									hidden
									accept="image/*"
									name="upload-image"
									randomizeFilename
									storageRef={this.storageRef}
									onUploadStart={this.handleUploadStart}
									onUploadError={this.handleUploadError}
									onUploadSuccess={this.handleUploadSuccess}
									onProgress={this.handleProgress}
									className={styles.changeImgFileSelector}
									maxWidth={maxWidth}
								/>
							</div>
						)}
					</div>
				)}
				{!(imageContent && imgInline) && (
					<div className={classnames(styles.surround, classNames.surround)}>
						{this.props.showHintText && !(!this.state.isUploading && this.state.progress === 100) &&
							<p className={styles.text}>{this.props.hintText !== undefined ? this.props.hintText : this.context.translate('Posts help you keep track of your Kit and who you were with when using it.')}</p>
						}
						<div className={classnames(styles.uploadWrapper, classNames.wrapper)}>
							{!(!this.state.isUploading && this.state.progress === 100) && (
								<Button variant="contained" classes={{ root: classnames(styles.uploadButton, classNames.buttonRoot) }}>
									{!this.state.isUploading && this.state.progress !== 100 && (
										<span>
											{this.props.buttonIconName ? (
												<Icon name={this.props.buttonIconName} className={classNames.buttonIcon} />
											) : (
												<ImageIcon className={styles.icon} />
											)}
											<span>{this.props.buttonText !== undefined ? this.props.buttonText : this.context.translate('Add photo')}</span>
										</span>
									)}
									{this.state.isUploading && (
										<span>
											<p>{this.props.uploadingText ? this.props.uploadingText : this.context.translate('Uploading photo')}</p>
											<LinearProgress variant="determinate" value={this.state.progress} className={classnames(styles.progress, classNames.progress)} />
										</span>
									)}
								</Button>
							)}
							{!this.state.isUploading && this.state.progress === 100 && (
								<span>
									<CloudDoneIcon className={styles.icon} /> <span>{this.props.uploadedText ? this.props.uploadedText : this.context.translate('Photo has been uploaded')}</span>
								</span>
							)}
							<FileUploader
								accept="image/*"
								name="upload-image"
								randomizeFilename
								storageRef={this.storageRef}
								onUploadStart={this.handleUploadStart}
								onUploadError={this.handleUploadError}
								onUploadSuccess={this.handleUploadSuccess}
								onProgress={this.handleProgress}
								maxWidth={maxWidth}
							/>
						</div>
					</div>
				)}
			</>
		)
	}
}

UploadImage.contextTypes = {
	translate: PropTypes.func
}

UploadImage.propTypes = {
	saveUploadedImage: PropTypes.func.isRequired,
	errorHandler: PropTypes.func.isRequired,
	classNames: PropTypes.object, 	// eslint-disable-line react/forbid-prop-types
	hintText: PropTypes.string,
	buttonIconName: PropTypes.string,
	showHintText: PropTypes.bool,
	buttonText: PropTypes.string,
	buttonChangeText: PropTypes.string,
	showChangeButton: PropTypes.bool,
	imageContent: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.arrayOf(PropTypes.string),
		PropTypes.shape({ type: PropTypes.string, webpurl: PropTypes.string, url: PropTypes.string }),
		PropTypes.arrayOf(PropTypes.shape({ type: PropTypes.string, webpurl: PropTypes.string, url: PropTypes.string }))
	]),
	imgClass: PropTypes.string,
	imgInline: PropTypes.bool,
	uploadingText: PropTypes.string,
	uploadedText: PropTypes.string,
	resetTimeout: PropTypes.number
}

UploadImage.defaultProps = {
	resetTimeout: 3000,
	classNames: undefined,
	hintText: undefined,
	buttonIconName: undefined,
	showHintText: true,
	buttonText: undefined,
	buttonChangeText: undefined,
	showChangeButton: true,
	imageContent: undefined,
	imgClass: undefined,
	imgInline: true,
	uploadingText: undefined,
	uploadedText: undefined
}

export default UploadImage



// WEBPACK FOOTER //
// ./src/components/UploadImage.jsx